import React, { useState, useEffect } from 'react';
import { 
    ArrowLeft, Calendar, FileText, Save, 
    Loader2, Building, Search, X, User, Phone // Phone icon added
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GET, POST } from '../services/api'; 

const CreateReferencePass = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    // --- Data State ---
    const [cities, setCities] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    // --- Form State ---
    const [formData, setFormData] = useState({
        city_id: '',
        chapter_id: '',
        member_id: '',       
        member_name: '',     
        member_image: '',    
        refName: '',         // NEW: Referral Name
        phone: '',           // NEW: Referral Phone
        date: new Date().toISOString().split('T')[0],
        remarks: ''
    });

    // --- Errors State ---
    const [errors, setErrors] = useState({});

    // --- 1. Fetch Cities ---
    useEffect(() => {
        const fetchCities = async () => {
            try {
                const res = await GET('/app/cities');
                if (res && res.success) {
                    setCities(res.data);
                }
            } catch (error) {
                console.error("Error fetching cities:", error);
            }
        };
        fetchCities();
    }, []);

    // --- 2. Fetch Chapters ---
    useEffect(() => {
        const fetchChapters = async () => {
            if (!formData.city_id) {
                setChapters([]);
                return;
            }
            try {
                const res = await GET(`/app/chapters/${formData.city_id}`);
                if (res && res.success) {
                    setChapters(res.data);
                } else {
                    setChapters([]); 
                }
            } catch (error) {
                console.error("Error fetching chapters:", error);
                setChapters([]);
            }
        };
        fetchChapters();
    }, [formData.city_id]);

    // --- 3. Member Search Logic ---
    useEffect(() => {
        if (formData.member_id) return; 

        const timer = setTimeout(async () => {
            if (formData.member_name.length > 2) {
                setIsSearching(true);
                try {
                    const res = await GET(`/app/search-businesses?search=${formData.member_name}`);
                    if (res && res.success) {
                        setSearchResults(res.data);
                    }
                } catch (error) {
                    console.error("Search error:", error);
                } finally {
                    setIsSearching(false);
                }
            } else {
                setSearchResults([]);
            }
        }, 500); 

        return () => clearTimeout(timer);
    }, [formData.member_name, formData.member_id]);


    // --- Handlers ---

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'city_id') {
            setFormData(prev => ({ ...prev, city_id: value, chapter_id: '' }));
        } else if (name === 'member_name') {
            setFormData(prev => ({ ...prev, member_name: value, member_id: '' }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSelectMember = (member) => {
        setFormData(prev => ({
            ...prev,
            member_id: member.user_id, 
            member_name: member.full_name,
            member_image: member.profile_picture
        }));
        setSearchResults([]); 
        if (errors.member_name) setErrors(prev => ({ ...prev, member_name: '' }));
    };

    const clearSelectedMember = () => {
        setFormData(prev => ({
            ...prev,
            member_id: '',
            member_name: '',
            member_image: ''
        }));
    };

    // --- Validation Logic ---
    const validateForm = () => {
        let tempErrors = {};
        if (!formData.city_id) tempErrors.city_id = "City is required";
        if (!formData.chapter_id) tempErrors.chapter_id = "Chapter is required";
        if (!formData.member_id) tempErrors.member_name = "Please select a member";
        if (!formData.refName) tempErrors.refName = "Referral Name is required"; // Validating Name
        if (!formData.phone) tempErrors.phone = "Phone number is required";      // Validating Phone
        if (!formData.date) tempErrors.date = "Date is required";
        
        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    // --- Submit Handler ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setLoading(true);

        // --- UPDATED PAYLOAD ---
        const payload = {
            to_member_id: formData.member_id,
            refName: formData.refName, // Added
            phone: formData.phone,     // Added
            date: formData.date,
            remarks: formData.remarks || undefined
        };

        try {
            const res = await POST('/app/create-reference-pass', payload);

            if (res && res.success) {
                alert("Reference Pass Created Successfully!");
                window.location.href = '/member/dashboard';
            } else {
                alert(res?.message || "Failed to create reference pass");
                setLoading(false);
            }
        } catch (error) {
            console.error("Submission Error:", error);
            alert(error?.message || "Something went wrong. Please try again.");
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8F9FA] font-sans pb-28 pt-6 relative">
            
            {/* --- HEADER --- */}
            <div className="container mx-auto px-4 max-w-lg">
                <div className="flex items-center gap-4 mb-8">
                    <button 
                        onClick={() => navigate(-1)} 
                        className="p-2.5 bg-white rounded-full shadow-sm text-gray-700 hover:text-[#1A87CF] transition-colors"
                    >
                        <ArrowLeft size={22} />
                    </button>
                    <h1 className="text-xl font-bold text-gray-900">Add Reference Pass</h1>
                </div>

                {/* --- FORM CARD --- */}
                <div className="bg-white rounded-[30px] p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 relative">
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                        
                        {/* 1. City & Chapter (Row) */}
                        <div className="grid grid-cols-2 gap-4">
                            {/* City */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">
                                    City <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <select
                                        name="city_id"
                                        value={formData.city_id}
                                        onChange={handleChange}
                                        className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 appearance-none text-sm font-medium outline-none transition-all
                                            ${errors.city_id ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}
                                        `}
                                    >
                                        <option value="">Select City</option>
                                        {cities.map(city => (
                                            <option key={city._id} value={city._id}>{city.name}</option>
                                        ))}
                                    </select>
                                    <Building size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                                {errors.city_id && <p className="text-xs text-red-500 mt-1 ml-1">{errors.city_id}</p>}
                            </div>

                            {/* Chapter */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">
                                    Chapter <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <select
                                        name="chapter_id"
                                        value={formData.chapter_id}
                                        onChange={handleChange}
                                        disabled={!formData.city_id} 
                                        className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 appearance-none text-sm font-medium outline-none transition-all
                                            ${errors.chapter_id ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}
                                            ${!formData.city_id ? 'opacity-50 cursor-not-allowed' : ''}
                                        `}
                                    >
                                        <option value="">Select Chapter</option>
                                        {chapters.map(chap => (
                                            <option key={chap._id} value={chap._id}>{chap.name}</option>
                                        ))}
                                    </select>
                                    <Building size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                                {errors.chapter_id && <p className="text-xs text-red-500 mt-1 ml-1">{errors.chapter_id}</p>}
                            </div>
                        </div>

                        {/* 2. Give To (Member Search) */}
                        <div className="relative">
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">
                                Give To <span className="text-red-500">*</span>
                            </label>
                            
                            <div className="relative">
                                {/* Left Icon */}
                                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                    {formData.member_image ? (
                                        <img 
                                            src={formData.member_image} 
                                            alt="User" 
                                            className="w-6 h-6 rounded-full object-cover border border-gray-200"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'block';
                                            }}
                                        />
                                    ) : null}
                                    <div style={{ display: formData.member_image ? 'none' : 'block' }}>
                                        <User size={20} />
                                    </div>
                                </div>

                                <input 
                                    type="text" 
                                    name="member_name"
                                    value={formData.member_name}
                                    onChange={handleChange}
                                    readOnly={!!formData.member_id} 
                                    placeholder="Search member name..."
                                    className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 text-sm font-medium outline-none transition-all pl-12 pr-10
                                        ${errors.member_name ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}
                                    `}
                                />

                                {formData.member_id && (
                                    <button 
                                        type="button"
                                        onClick={clearSelectedMember}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full bg-gray-200 text-gray-600 hover:bg-red-50 hover:text-red-500"
                                    >
                                        <X size={16} />
                                    </button>
                                )}

                                {isSearching && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <Loader2 size={18} className="animate-spin text-[#1A87CF]" />
                                    </div>
                                )}
                            </div>

                            {/* Dropdown Results */}
                            {searchResults.length > 0 && !formData.member_id && (
                                <div className="absolute z-10 w-full mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 max-h-60 overflow-y-auto">
                                    {searchResults.map((user) => (
                                        <div 
                                            key={user._id}
                                            onClick={() => handleSelectMember(user)}
                                            className="flex items-center gap-3 p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0 transition-colors"
                                        >
                                            {user.profile_picture ? (
                                                <img 
                                                    src={user.profile_picture} 
                                                    alt="profile" 
                                                    className="w-10 h-10 rounded-full object-cover border border-gray-200 bg-gray-100"
                                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                />
                                            ) : null}
                                            <div 
                                                className="w-10 h-10 rounded-full border border-gray-200 bg-gray-100 flex items-center justify-center text-gray-400"
                                                style={{ display: user.profile_picture ? 'none' : 'flex' }}
                                            >
                                                <User size={20} />
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-gray-800">{user.full_name}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {errors.member_name && <p className="text-xs text-red-500 mt-1 ml-1">{errors.member_name}</p>}
                        </div>

                        {/* --- NEW SECTION: REFERRAL DETAILS --- */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Referral Name */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">
                                    Referral Name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input 
                                        type="text" 
                                        name="refName"
                                        value={formData.refName}
                                        onChange={handleChange}
                                        placeholder="Name of person..."
                                        className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 text-sm font-medium outline-none transition-all pl-11
                                            ${errors.refName ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}
                                        `}
                                    />
                                    <User size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"/>
                                </div>
                                {errors.refName && <p className="text-xs text-red-500 mt-1 ml-1">{errors.refName}</p>}
                            </div>

                            {/* Referral Phone */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">
                                    Referral Phone <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input 
                                        type="tel" 
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="Phone number..."
                                        className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 text-sm font-medium outline-none transition-all pl-11
                                            ${errors.phone ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}
                                        `}
                                    />
                                    <Phone size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"/>
                                </div>
                                {errors.phone && <p className="text-xs text-red-500 mt-1 ml-1">{errors.phone}</p>}
                            </div>
                        </div>

                        {/* 3. Date */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">
                                Date <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input 
                                    type="date" 
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 text-sm font-medium outline-none transition-all pl-11
                                        ${errors.date ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}
                                    `}
                                />
                                <Calendar size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"/>
                            </div>
                            {errors.date && <p className="text-xs text-red-500 mt-1 ml-1">{errors.date}</p>}
                        </div>

                        {/* 4. Remarks */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">
                                Remarks
                            </label>
                            <div className="relative">
                                <textarea 
                                    name="remarks"
                                    value={formData.remarks}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="Enter reference details..."
                                    className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 
                                        ${errors.remarks ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}
                                        text-sm font-medium outline-none transition-all resize-none pl-11`}
                                ></textarea>
                                <FileText size={20} className="absolute left-3.5 top-4 text-gray-400"/>
                            </div>
                            {errors.remarks && <p className="text-xs text-red-500 mt-1 ml-1">{errors.remarks}</p>}
                        </div>

                        {/* Submit Button */}
                        <button 
                            type="submit" 
                            disabled={loading}
                            className="w-full py-4 bg-[#1A87CF] text-white rounded-2xl font-bold text-sm shadow-lg shadow-blue-200/50 hover:shadow-xl hover:shadow-blue-200/70 hover:-translate-y-0.5 transition-all flex justify-center items-center gap-2 mt-4"
                        >
                            {loading ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
                            {loading ? 'Submitting...' : 'Save Reference Pass'}
                        </button>

                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateReferencePass;